import { useState, useCallback, useRef, useEffect } from "react";
import type { Message, WidgetConfig } from "../../shared/types";
import { ApiClient, type ApiError } from "../api/client";
import {
  EventType,
  type ToolCallStartEvent,
  type ToolCallEndEvent,
} from "../api/agui-protocol";
import {
  getConversationId,
  setConversationId as saveConversationId,
  touchConversation,
  getStoredMessages,
  setStoredMessages,
  clearConversationData,
} from "../utils/storage";
import { trackWidgetEvent, trackError } from "../../telemetry/events";
import { getEnvironmentData } from "../../telemetry/environment";
import {
  useStatusQueue,
  DEFAULT_STATUS_TIMING,
  type StatusTimingConfig,
} from "./useStatusQueue";
export {
  DEFAULT_STATUS_TIMING,
  type StatusTimingConfig,
} from "./useStatusQueue";

interface UseConversationResult {
  messages: Message[];
  isLoading: boolean;
  statusMessage: string | null;
  conversationId: string | null;
  sendMessage: (content: string) => Promise<void>;
  retryMessage: (messageId: string) => Promise<void>;
}

export function useConversation(
  widgetConfig: WidgetConfig,
  welcomeMessage?: string | null,
  statusTiming: StatusTimingConfig = DEFAULT_STATUS_TIMING,
): UseConversationResult {
  const {
    statusMessage,
    enqueueStatus,
    clearStatusQueue,
    cancelEscalation,
    scheduleEscalation,
  } = useStatusQueue(statusTiming);
  const [messages, setMessages] = useState<Message[]>(() => {
    const stored = getStoredMessages(widgetConfig.agentId);
    if (stored && stored.length > 0) {
      return stored;
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(() =>
    getConversationId(widgetConfig.agentId),
  );
  const pendingRetryRef = useRef<string | null>(null);
  const streamingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const welcomeMessageInjected = useRef(false);
  const broadcastRef = useRef<BroadcastChannel | null>(null);
  const isHydrating = useRef(true);

  useEffect(() => {
    const stored = getStoredMessages(widgetConfig.agentId);
    if (stored && stored.length > 0) {
      welcomeMessageInjected.current = true;
    }
    isHydrating.current = false;
  }, [widgetConfig.agentId]);

  useEffect(() => {
    if (
      welcomeMessage &&
      !welcomeMessageInjected.current &&
      messages.length === 0
    ) {
      const welcomeMsg: Message = {
        id: "welcome-message",
        role: "assistant",
        content: welcomeMessage,
        timestamp: Date.now(),
      };
      setMessages([welcomeMsg]);
      welcomeMessageInjected.current = true;
    }
  }, [welcomeMessage, messages.length]);

  useEffect(() => {
    return () => {
      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
      }
      clearStatusQueue();
    };
  }, [clearStatusQueue]);

  useEffect(() => {
    if (!conversationId || messages.length === 0) return;

    setStoredMessages(widgetConfig.agentId, messages);

    if (isHydrating.current || !broadcastRef.current) return;

    broadcastRef.current.postMessage({
      type: "messages:update",
      agentId: widgetConfig.agentId,
      conversationId,
      messages,
    });
  }, [messages, conversationId, widgetConfig.agentId]);

  useEffect(() => {
    if (typeof window === "undefined" || !("BroadcastChannel" in window)) {
      return;
    }

    const channelName = `resident-agent:${widgetConfig.agentId}:conversation`;
    const channel = new BroadcastChannel(channelName);
    broadcastRef.current = channel;

    channel.onmessage = (event) => {
      const {
        type,
        agentId: incomingAgentId,
        messages: incomingMessages,
        conversationId: incomingId,
      } = event.data || {};

      if (incomingAgentId !== widgetConfig.agentId) {
        console.warn(
          `[useConversation] Ignoring broadcast from different agent: ${incomingAgentId}`,
        );
        return;
      }

      if (type === "messages:clear") {
        setMessages([]);
        setConversationId(null);
        welcomeMessageInjected.current = false;
        return;
      }

      if (type === "conversationId:update" && incomingId) {
        if (!conversationId || conversationId !== incomingId) {
          setConversationId(incomingId);
          saveConversationId(widgetConfig.agentId, incomingId);
        }
        return;
      }

      if (
        type === "messages:update" &&
        incomingId === conversationId &&
        Array.isArray(incomingMessages)
      ) {
        setMessages((prev) => {
          const prevJson = JSON.stringify(prev);
          const incomingJson = JSON.stringify(incomingMessages);
          return prevJson === incomingJson ? prev : incomingMessages;
        });
      }
    };

    const pollInterval = setInterval(() => {
      const storedId = getConversationId(widgetConfig.agentId);
      if (storedId && storedId !== conversationId) {
        setConversationId(storedId);
      }

      const storedMessages = getStoredMessages(widgetConfig.agentId);
      if (storedMessages && storedMessages.length > 0) {
        setMessages((prev) => {
          const prevJson = JSON.stringify(prev);
          const storedJson = JSON.stringify(storedMessages);
          return prevJson === storedJson ? prev : storedMessages;
        });
      }
    }, 5000);

    return () => {
      clearInterval(pollInterval);
      channel.close();
      broadcastRef.current = null;
    };
  }, [conversationId, widgetConfig.agentId]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
        streamingTimeoutRef.current = null;
      }

      clearStatusQueue();
      enqueueStatus("Thinking...");

      const handleStatusEvent = (
        event: ToolCallStartEvent | ToolCallEndEvent,
      ) => {
        if (event.type === EventType.TOOL_CALL_START) {
          cancelEscalation(); // a second tool call must not inherit a stale escalation timer from the previous one
          enqueueStatus("Fetching official resources");
        } else if (event.type === EventType.TOOL_CALL_END) {
          enqueueStatus("Analyzing results", () => {
            scheduleEscalation("Reviewing the details");
          });
        }
      };

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: "user",
        content: content.trim(),
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      touchConversation(widgetConfig.agentId);

      const messageSentAttributes: Record<string, string | number> = {
        messageId: userMessage.id,
        contentLength: content.trim().length,
        sourceUrl: widgetConfig.sourceUrl || "unknown",
        agentId: widgetConfig.agentId,
      };
      if (conversationId) {
        messageSentAttributes.conversationId = conversationId;
      }
      if (widgetConfig.organizationId) {
        messageSentAttributes.organizationId = widgetConfig.organizationId;
      }
      trackWidgetEvent("message.sent", messageSentAttributes);

      const streamingMessageId = `assistant-${Date.now()}`;
      const streamingMessage: Message = {
        id: streamingMessageId,
        role: "assistant",
        content: "",
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, streamingMessage]);

      let chunkQueue: string[] = [];
      let isProcessing = false;
      let responseStartTracked = false;
      const responseStartTime = Date.now();

      const processQueue = () => {
        if (chunkQueue.length === 0) {
          isProcessing = false;
          streamingTimeoutRef.current = null;
          return;
        }

        isProcessing = true;
        const chunk = chunkQueue.shift()!;

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === streamingMessageId
              ? { ...msg, content: msg.content + chunk }
              : msg,
          ),
        );

        streamingTimeoutRef.current = setTimeout(processQueue, 30);
      };

      try {
        const apiClient = new ApiClient(
          widgetConfig.apiUrl,
          widgetConfig.agentId,
        );

        const response = await apiClient.sendMessage(
          content,
          conversationId,
          (chunk) => {
            if (!responseStartTracked) {
              enqueueStatus(null);
              const responseStartAttributes: Record<string, string> = {
                messageId: streamingMessageId,
                sourceUrl: widgetConfig.sourceUrl || "unknown",
                agentId: widgetConfig.agentId,
              };
              if (conversationId) {
                responseStartAttributes.conversationId = conversationId;
              }
              if (widgetConfig.organizationId) {
                responseStartAttributes.organizationId =
                  widgetConfig.organizationId;
              }
              trackWidgetEvent(
                "message.response.start",
                responseStartAttributes,
              );
              responseStartTracked = true;
            }

            chunkQueue.push(chunk);
            if (!isProcessing) {
              processQueue();
            }
          },
          handleStatusEvent,
        );

        if (!conversationId && response.conversationId) {
          setConversationId(response.conversationId);
          saveConversationId(widgetConfig.agentId, response.conversationId);

          if (broadcastRef.current) {
            broadcastRef.current.postMessage({
              type: "conversationId:update",
              agentId: widgetConfig.agentId,
              conversationId: response.conversationId,
            });
          }

          const envData = getEnvironmentData();
          const conversationCreatedAttributes: Record<
            string,
            string | number | boolean
          > = {
            conversationId: response.conversationId,
            // Include firstMessageId to link the first message (sent before conversationId existed)
            // This allows App Insights queries to find all messages by checking:
            // conversationId == X OR messageId == firstMessageId
            firstMessageId: userMessage.id,
            sourceUrl: widgetConfig.sourceUrl || "unknown",
            agentId: widgetConfig.agentId,
            ...envData,
          };
          if (widgetConfig.organizationId) {
            conversationCreatedAttributes.organizationId =
              widgetConfig.organizationId;
          }
          trackWidgetEvent(
            "conversation.created",
            conversationCreatedAttributes,
          );
        }

        const responseEndAttributes: Record<string, string | number> = {
          messageId: streamingMessageId,
          durationMs: Date.now() - responseStartTime,
          sourceUrl: widgetConfig.sourceUrl || "unknown",
          agentId: widgetConfig.agentId,
        };
        const finalConversationId = response.conversationId || conversationId;
        if (finalConversationId) {
          responseEndAttributes.conversationId = finalConversationId;
        }
        if (widgetConfig.organizationId) {
          responseEndAttributes.organizationId = widgetConfig.organizationId;
        }
        trackWidgetEvent("message.response.end", responseEndAttributes);
      } catch (err) {
        // Remove the streaming message
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== streamingMessageId),
        );

        // Create error message
        const apiError = err as ApiError;
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          role: "assistant",
          content:
            apiError.userMessage || "Something went wrong. Please try again.",
          timestamp: Date.now(),
          error: true,
          errorType: apiError.errorType || "unknown",
          retryable: apiError.retryable !== false,
          originalUserMessage: content.trim(),
        };

        setMessages((prev) => [...prev, errorMessage]);

        const errorAttributes: Record<string, string> = {
          source: "message_send",
          messageId: userMessage.id,
          agentId: widgetConfig.agentId,
          errorType: apiError.errorType || "unknown",
        };
        if (conversationId) {
          errorAttributes.conversationId = conversationId;
        }
        if (widgetConfig.organizationId) {
          errorAttributes.organizationId = widgetConfig.organizationId;
        }
        trackError(err, errorAttributes);
      } finally {
        clearStatusQueue();
        setIsLoading(false);
      }
    },
    [
      clearStatusQueue,
      enqueueStatus,
      cancelEscalation,
      scheduleEscalation,
      conversationId,
      widgetConfig.agentId,
      widgetConfig.apiUrl,
      widgetConfig.organizationId,
      widgetConfig.sourceUrl,
    ],
  );

  const retryMessage = useCallback(
    async (messageId: string) => {
      const errorMessage = messages.find(
        (msg) => msg.id === messageId && msg.error,
      );
      if (!errorMessage || !errorMessage.originalUserMessage) {
        return;
      }

      if (pendingRetryRef.current === messageId) {
        return; // Prevent duplicate retries
      }

      pendingRetryRef.current = messageId;

      // Remove the error message
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));

      try {
        // Retry sending the original message
        await sendMessage(errorMessage.originalUserMessage);
      } finally {
        pendingRetryRef.current = null;
      }
    },
    [messages, sendMessage],
  );

  return {
    messages,
    isLoading,
    statusMessage,
    conversationId,
    sendMessage,
    retryMessage,
  };
}
